import { useMemo } from 'react'
import { useAuthStore } from '@/shared/stores/auth.store'
import {
  RbacModule as M,
  RbacAction as A,
  RbacScope as S,
  hasPerm,
  hasAnyAllScope,
  resolveHomeRoute,
  type ModuleKey,
  type ActionKey,
  type ScopeKey,
} from './permissions'

/**
 * Feature-level access gates.
 *
 * This is a shim layer kept alive from the pre-refactor RBAC model so the
 * existing FE doesn't have to change in lockstep with the BE schema (Phase 1).
 * Each gate is mapped to one or more new (module, action, scope) checks
 * against the post-refactor catalog. Phase 3 will replace this entire surface
 * with a module-driven `useAccess().can(module, action)` API.
 *
 * Gate semantics:
 *  • A gate is `true` if the user can at least *read* the underlying module
 *    (since `edit` implies `read`).
 *  • Gates that previously checked feature-specific actions (e.g. canBulkAssign
 *    needed `edit:client`) now require explicit `edit` permission on the
 *    relevant module.
 *  • Gates for dropped modules (Coding Manager / Compliance / Revenue / CDI)
 *    are wired to `false` — those features are not part of the new schema.
 */
export interface AccessGates {
  /** Chart queue / workspace pages — chart_queue read on own facilities. */
  canAccessWorkspace: boolean
  /** Charts dashboard — charts_dashboard read on own facilities. */
  canAdminClient: boolean
  /** Analytics dashboard — analytics_dashboard read. */
  canAdminEngine: boolean
  /** System admin pages — system_admin read on all (super-admin only). */
  canAdminSystem: boolean
  /** Bulk-assign encounters — chart_queue edit. */
  canBulkAssign: boolean
  /** Cross-client visibility — any permission held at scope='all'. */
  hasCrossClientAccess: boolean
  /** Can view facilities (any workspace or dashboard access). */
  canViewFacilities: boolean
  /** Can view the Clients utility — clients module read. */
  canViewClients: boolean
  /** Notes — notes module read. */
  canAccessNotes: boolean
  /** Prompt config — prompt_config edit. */
  canConfigurePrompts: boolean
  /** Client section (Module Registry + Client Configuration) — client_config read. */
  canConfigureClient: boolean
  /** Can view engine admin data — analytics_dashboard read. */
  canViewEngineData: boolean
  /** Operations dashboard — operations_dashboard read on own facilities. */
  canViewOperations: boolean
  /** Can raise a coder→client query — chart_queue edit. */
  canRaiseCoderQuery: boolean
  /**
   * Queue buckets the user is allowed to see. In the shim, this still returns
   * ALL_BUCKETS — the proper per-role bucket fetch lands in Phase 2 once
   * /api/v1/auth/buckets exists.
   */
  visibleBuckets: Set<QueueBucket>
  /** Can soft/hard-delete archived encounters — history edit on all. */
  canDeleteEncounters: boolean
  /** Can create/edit/delete KB reference data at all scope — knowledge_base edit:all (cross-client / super-admin). */
  canEditKnowledgeBase: boolean
  /**
   * Can create a manual chart — admin (system or client) with chart_queue edit.
   * Composite gate: mirrors the "New Chart" button so the route guard on
   * /workspace/queue/create stays identical to the control that opens it.
   */
  canCreateManualChart: boolean
  /**
   * Can view the internal / cross-client analytics surfaces — the engine-admin
   * Costs (spend) and Flag Metrics (internal audit) tabs and their routes.
   * Gated on analytics_dashboard read at scope 'all', so super admins, the
   * engine-admin role, and the cross-client Prod Read-Only role qualify, while
   * regular client users (own-scope analytics) do not.
   */
  canViewEngineInternals: boolean
  /**
   * Updates & Announcements reader access — `updates` read AND the
   * server-driven `announcements_module` feature flag (kill-switch). Gates the
   * sidebar item and reader routes.
   */
  canViewAnnouncements: boolean
  /**
   * Can compose/manage announcements — `updates` edit at scope 'all'
   * (super_admin) AND the feature flag. Gates the admin composer + entry.
   */
  canComposeAnnouncements: boolean
  /**
   * True when the signed-in user is a SaaS coder (role `coder`) and NOT also a
   * client_admin. Role-name based (not permission based) because coders and
   * client_admins share the same chart_queue scope — the distinction is the
   * persona. Used for coder-only UX restrictions, e.g. coders may see received
   * charts in the queue list but cannot open them.
   */
  isCoder: boolean
}

export type QueueBucket =
  | 'received'
  | 'copilot'
  | 'autopilot'
  | 'coding_complete'
  | 'exported'
  | 'query'
  | 'exceptions'

const ALL_BUCKETS: QueueBucket[] = [
  'received',
  'copilot',
  'autopilot',
  'coding_complete',
  'exported',
  'query',
  'exceptions',
]

export interface AccessInfo extends AccessGates {
  /** Role-aware landing page path. */
  homeRoute: string
  /**
   * Module-based permission check — the recommended API for new code.
   * Returns true if the user has at least the requested (action, scope)
   * on the given module. 'edit' implies 'read'; 'all' implies 'own'.
   *
   * Example:
   *   const { can } = useAccess()
   *   if (can('chart_queue', 'edit')) { ... }
   *   if (can('history', 'edit', 'all')) { ... }
   *
   * Default scope is 'own'. The 22 legacy `canAccess*` flags on this same
   * object remain available; new components should prefer `.can()`.
   */
  can: (module: ModuleKey, action?: ActionKey, scope?: ScopeKey) => boolean
}

/**
 * Central access hook — the single entry point for all RBAC checks in the UI.
 *
 * Usage:
 *   const { canAccessWorkspace, canAdminSystem, homeRoute } = useAccess()
 */
export function useAccess(): AccessInfo {
  const user = useAuthStore((s) => s.user)
  const cachedPermissions = useAuthStore((s) => s.cachedPermissions)

  return useMemo(() => {
    const perms = user?.permissions ?? (cachedPermissions.length > 0 ? cachedPermissions : [])
    const check = (mod: typeof M[keyof typeof M], act: typeof A[keyof typeof A], scp: typeof S[keyof typeof S]) =>
      hasPerm(perms, mod, act, scp)

    return {
      canAccessWorkspace:   check(M.ChartQueue,          A.Read, S.Own),
      canAdminClient:       check(M.ChartsDashboard,     A.Read, S.Own),
      canAdminEngine:       check(M.AnalyticsDashboard,  A.Read, S.Own),
      canAdminSystem:       check(M.SystemAdmin,         A.Read, S.All),
      canBulkAssign:        check(M.ChartQueue,          A.Edit, S.Own),
      hasCrossClientAccess: hasAnyAllScope(perms),
      canViewFacilities:    check(M.ChartsDashboard,     A.Read, S.Own)
                          || check(M.ChartQueue,         A.Read, S.Own),
      // 'own' is the minimum bar — super_admin satisfies via scope hierarchy
      // ('all' implies 'own'). client_admin gets 'own' from migration
      // 20260529_007 so they can browse the clients they belong to.
      canViewClients:       check(M.Clients,             A.Read, S.Own),
      canAccessNotes:       check(M.Notes,               A.Read, S.Own),
      canConfigurePrompts:  check(M.PromptConfig,        A.Edit, S.Own),
      canConfigureClient:   check(M.ClientConfig,        A.Read, S.All),
      // Engine data (prediction-trail internals) is a cross-client capability,
      // gated on chart_queue:read:all — kept in lockstep with the BE
      // includeEngineData gate so the coder's analytics_dashboard:read:own
      // grant never surfaces engine internals in the workspace.
      canViewEngineData:    check(M.ChartQueue,           A.Read, S.All),
      canViewOperations:    check(M.OperationsDashboard, A.Read, S.Own),
      canRaiseCoderQuery:   check(M.ChartQueue,          A.Edit, S.Own),
      canDeleteEncounters:  check(M.History,             A.Edit, S.All),
      canEditKnowledgeBase: check(M.KnowledgeBase,        A.Edit, S.All),

      // Composite gates — kept identical to the UI controls that lead to these
      // routes so the route guard and the button/tab stay in lockstep.
      // canCreateManualChart: admin (system OR client) AND chart_queue edit —
      // a read-only admin (charts_dashboard read only) must NOT qualify.
      canCreateManualChart:
        (check(M.SystemAdmin, A.Read, S.All) || check(M.ChartsDashboard, A.Read, S.Own)) &&
        check(M.ChartQueue, A.Edit, S.Own),
      // canViewEngineInternals — cross-client analytics read (scope 'all').
      canViewEngineInternals: check(M.AnalyticsDashboard, A.Read, S.All),
      // canViewAnnouncements — `updates` read AND the server-driven feature flag.
      // Both must hold: permission alone isn't enough while the kill-switch is off.
      canViewAnnouncements:       check(M.Announcements, A.Read, S.Own) && Boolean(user?.features?.announcements_module),
      canComposeAnnouncements:    check(M.Announcements, A.Edit, S.All) && Boolean(user?.features?.announcements_module),

      // Bucket visibility — driven by t_iam_role_buckets via /auth/me. If the
      // server hasn't returned a buckets list yet (legacy session, cached
      // login), fall back to ALL_BUCKETS so the UI doesn't blank out.
      visibleBuckets:       user?.buckets && user.buckets.length > 0
        ? new Set(user.buckets as QueueBucket[])
        : new Set(ALL_BUCKETS),

      homeRoute:            resolveHomeRoute(perms),

      // Persona flag — role-name based (see AccessGates.isCoder docstring).
      isCoder: (user?.roles ?? []).some((r) => r.name === 'coder')
            && !(user?.roles ?? []).some((r) => r.name === 'client_admin'),

      // Module-based check — new API. Defaults: action='read', scope='own'.
      can: (module: ModuleKey, action: ActionKey = A.Read, scope: ScopeKey = S.Own) =>
        hasPerm(perms, module, action, scope),
    }
  }, [user?.permissions, user?.buckets, user?.roles, user?.features?.announcements_module, cachedPermissions])
}
